import React, { useEffect, useState } from 'react';
import { ButtonRounded, TextInput } from 'is24-corecss';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiResponseError } from '../../api/ApiResponseError';
import { startWebIdVerification } from '../../api/webid/webIdVerification';
import { WebIdVerificationAttempt } from '../../api/webid/model/WebIdVerificationAttempt';
import { trackEvent } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import { useTvsState } from '../../utils/useTvsState';
import {
  normalizeBirthdate,
  validateBirthdate,
  validateFirstName,
  validateLastName,
  validatePhoneNumber,
} from '../../pages/verificationLandingPage/validators/Validators';

interface WebIdVerificationProps {
  onContentChange: (content: React.ReactNode) => void;
  onError: (error: string) => void;
  setPhoneNumberForTan: (phoneNumber: string | null) => void;
}

const WebIdVerification: React.FC<WebIdVerificationProps> = ({ onContentChange, onError, setPhoneNumberForTan }) => {
  const { t } = useTranslation();
  const { clientId } = useClientConfig();
  const [, setTvsState] = useTvsState();
  const [, setSearchParams] = useSearchParams();
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [birthdateError, setBirthdateError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');

  useEffect(() => {
    onContentChange(
      <div className='grid grid-flex gutter-horizontal-m margin-top-m'>
        <div className='grid-item one-half palm-one-whole palm-margin-bottom-l'>
          <TextInput
            data-testid='first-name-input'
            id='first-name'
            label={t('VerificationLandingPage.inputLabelFName')}
            mandatory={true}
            onChange={(event) => setFirstName(event.target.value)}
            valid={firstNameError.length === 0}
            captionText={firstNameError}
            placeholder='Max'
          />
        </div>
        <div className='grid-item one-half palm-one-whole margin-bottom-l'>
          <TextInput
            data-testid='last-name-input'
            id='last-name'
            label={t('VerificationLandingPage.inputLabelLName')}
            mandatory={true}
            onChange={(event) => setLastName(event.target.value)}
            valid={lastNameError.length === 0}
            captionText={lastNameError}
            placeholder='Mustermann'
          />
        </div>
        <div className='grid-item one-half palm-one-whole margin-bottom-l'>
          <TextInput
            data-testid='birthdate-input'
            id='with-date-state'
            label={t('VerificationLandingPage.inputLabelBirthdate' as any)}
            placeholder='DD.MM.YYYY'
            type='date'
            mandatory={true}
            onChange={(event) => setBirthdate(event.target.value)}
            valid={birthdateError.length === 0}
            captionText={birthdateError}
          />
        </div>
        <div className='grid-item one-half palm-one-whole'>
          <TextInput
            data-testid='phone-number-input'
            id='for telephone numbers'
            type='tel'
            label={t('VerificationLandingPage.inputLabelPhoneNumber' as any)}
            placeholder='+49 170 1234567'
            mandatory={true}
            onChange={(event) => setPhoneNumber(event.target.value)}
            valid={phoneNumberError.length === 0}
            captionText={phoneNumberError}
          />
        </div>
        <div className='grid grid-flex font-body-small-regular font-lightgray padding-left-m margin-top-xxl palm-margin-top-xl palm-margin-bottom-l'>
          {t('VerificationLandingPage.webIdHelperText' as any)}
        </div>
      </div>,
    );
  }, [birthdateError, firstNameError, lastNameError, onContentChange, phoneNumberError, t]);

  const validateUserInput = () => {
    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);
    const isBirthdateValid = validateBirthdate(birthdate);
    const isPhoneNumberValid = validatePhoneNumber(phoneNumber);

    setFirstNameError(isFirstNameValid ? '' : t('VerificationLandingPage.firstNameError'));
    setLastNameError(isLastNameValid ? '' : t('VerificationLandingPage.lastNameError'));
    setBirthdateError(isBirthdateValid ? '' : t('VerificationLandingPage.birthdateError' as any));
    setPhoneNumberError(isPhoneNumberValid ? '' : t('VerificationLandingPage.phoneNumberError' as any));

    return isFirstNameValid && isLastNameValid && isBirthdateValid && isPhoneNumberValid;
  };

  const onWebIdVerificationClick = async () => {
    if (!validateUserInput()) {
      return;
    }
    const webIdVerificationData: WebIdVerificationAttempt = {
      firstName: firstName,
      lastName: lastName,
      dateOfBirth: toIsoBirthdate(normalizeBirthdate(birthdate)),
      mobileNumber: phoneNumber,
      triggeredBy: clientId,
    };
    setIsRequestPending(true);

    try {
      trackEvent('is24.de.sicherheit.verifizierung.modal', 'verification_started', 'user', clientId, 'webid');

      const startVerificationResponse = await startWebIdVerification(webIdVerificationData);
      if (startVerificationResponse.profileExist || startVerificationResponse.isprfiecheck) {
        setPhoneNumberForTan(phoneNumber.trim());
        setTvsState('verification_webid_tan');
        return;
      }
      if (startVerificationResponse.sessionId) {
        setSearchParams(
          {
            tvs_state: 'webid_verification_start',
            webid_session_id: startVerificationResponse.sessionId,
          },
          { replace: true },
        );
        trackEvent('is24.de.sicherheit.verifizierung.modal', 'webid_profile_not_found', 'user', clientId, 'webid');
        return;
      }
      onError('errors.default');
    } catch (error) {
      if (error instanceof ApiResponseError) {
        if (error.responseStatus === 400) {
          onError('errors.webid.bad.request' as any);
        } else if (error.responseStatus === 429) {
          onError('errors.webid.request.timeout' as any);
        } else if (error.responseStatus === 401) {
          onError('errors.unauthorized');
        } else {
          onError('errors.default');
        }
      } else {
        onError('errors.default');
      }
    } finally {
      setIsRequestPending(false);
    }
  };

  const toIsoBirthdate = (normalizedBirthdate: string): string => {
    const match = normalizedBirthdate.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (!match) {
      return normalizedBirthdate;
    }
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  };

  return (
    <div className='modal-footer verification-modal-footer'>
      <div className='grid grid-flex'>
        <div className='grid-item grid-align-center one-half palm-one-whole'></div>
        <div className='grid-item one-half palm-one-whole'>
          <div className='grid-flex grid-justify-end verification-footer-div palm-one-whole'>
            <ButtonRounded
              data-testid='webid-verification-button'
              className='verification-footer-button'
              loading={isRequestPending}
              label={t('VerificationLandingPage.buttonText')}
              size='medium'
              onClick={onWebIdVerificationClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebIdVerification;
